/**
 * Hook for fetching notifications by application reference
 * Simple hook that only fetches notifications linked to specific application(s)
 */

import { useMemo } from 'react';
import type { DrupalNotification, UseNotificationsResult } from '../types';

/**
 * Custom hook dependency - must be provided by the consuming application
 */
export interface UseDrupalCollectionHook {
  <T = Record<string, unknown>>(
    resourceType: string,
    searchParams?: Record<string, string> | undefined
  ): {
    items: T[];
    isLoading: boolean;
    error: string | null;
  };
}

/**
 * Hook options for notifications by application
 */
export interface UseNotificationsByApplicationOptions {
  /** Application name(s) to filter notifications by (field_app_name value) */
  applicationName: string | string[];
  /** Maximum number of notifications to fetch */
  maxNotifications?: number;
  /** Custom Drupal collection hook implementation */
  useDrupalCollection: UseDrupalCollectionHook;
}

/**
 * Creates query parameters for fetching notifications by application name
 */
function createApplicationParams(
  applicationName: string | string[],
  maxNotifications: number = 5
): Record<string, string> {
  const sp = new URLSearchParams();

  // Fields to retrieve
  sp.set(
    'fields[node--notifications]',
    [
      'title',
      'field_html',
      'created',
      'changed',
      'field_start',
      'field_end',
      'field_notified_users',
      'field_applications',
      'field_variant',
    ].join(',')
  );
  sp.set('fields[node--application]', 'title,field_app_name');
  sp.set('include', 'field_applications');
  sp.set('filter[status]', '1');
  sp.set('sort', '-created');
  sp.set('page[limit]', String(maxNotifications));

  // Filter by application name(s) via field_app_name
  const appNames = Array.isArray(applicationName) ? applicationName : [applicationName];

  if (appNames.length === 1) {
    sp.set('filter[field_applications.field_app_name]', appNames[0]);
  } else if (appNames.length > 1) {
    sp.set('filter[field_applications.field_app_name][operator]', 'IN');
    appNames.forEach((appName, index) => {
      sp.set(`filter[field_applications.field_app_name][value][${index}]`, appName);
    });
  }

  return Object.fromEntries(sp.entries());
}

/**
 * Hook to fetch notifications by application name (field_app_name)
 *
 * @example
 * ```tsx
 * const { items, isLoading, error } = useNotificationsByApplication({
 *   applicationName: 'sustainability-hub',
 *   maxNotifications: 10,
 *   useDrupalCollection,
 * });
 * ```
 */
export function useNotificationsByApplication(
  options: UseNotificationsByApplicationOptions
): UseNotificationsResult {
  const {
    applicationName,
    maxNotifications = 5,
    useDrupalCollection,
  } = options;

  // Build query params
  const queryParams = useMemo(() => {
    // If no applicationName provided, return undefined to disable the query
    const appNames = Array.isArray(applicationName) ? applicationName : [applicationName];
    if (appNames.length === 0 || appNames.every(name => !name)) {
      return undefined;
    }
    return createApplicationParams(applicationName, maxNotifications);
  }, [applicationName, maxNotifications]);

  // Fetch notifications
  const { items, isLoading, error } = useDrupalCollection<DrupalNotification>(
    'node--notifications',
    queryParams
  );

  return {
    items: items || [],
    isLoading,
    error: error ? new Error(error) : null,
  };
}
